var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// elements/components/x-formfield-wrapper.ts
import { html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { ChemistryLitElement } from "../../chemistry-lit-element.js";
import { css } from "../../directives/css.js";
import { inputToken } from "../theming/input-token.js";
import { xComponents } from "../x-components.js";
import "./x-flex.js";
import "./x-icon.js";
var XFormFieldWrapper = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.transparent = false;
    this.placeholder = "";
    this.placeholderMode = this.defaults.placeholderMode;
    this.icon = "";
    this.clearable = false;
    this.disabled = false;
    this.postfix = "";
    this.showLabel = false;
    this.multiLine = false;
    this.error = "";
    this.plain = false;
  }
  get inputToken() {
    return inputToken(this);
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          height: "unset",
          fontSize: this.baseFontSize
        },
        ":host ::slotted(*)": {
          flex: "1 1 0%"
        },
        ":host [background]": {
          ...this.inputToken,
          background: !!this.transparent ? "transparent" : `var(--color-base-200, ${this.colors.neutral.shade(50)})`,
          height: this.multiLine ? "auto" : `calc(${this.baseFontSize}px + ${this.inputToken.paddingTop} + ${this.inputToken.paddingBottom} + 2px)`
        },
        ":host [background]:hover": {
          background: !!this.transparent ? "transparent" : `var(--color-base-300, ${this.colors.neutral.shade(100)})`
        },
        ':host([placeholder][placeholder-mode="inline-floating"]) ::slotted(*)': {
          transform: this.plain ? "0" : this.showLabel ? "translateY(0.5em) " : "translateY(0.15em)"
        },
        ":host([error]) [background]": {
          borderColor: this.colors?.tone.error,
          background: this.colors?.tone.error.shade(50)
        },
        ":host([disabled])": { opacity: ".5", pointerEvents: "none" }
      }
    ];
  }
  render() {
    const plain = html`
      ${this._prefixLabel()}
      <slot></slot>
      ${this.postfix ? html` <x-text tone="subtle"> ${this.postfix} </x-text> ` : nothing} ${this.clearableIcon()}
      ${this.iconDisplay()}
    `;
    if (this.plain)
      return xComponents(html` <x-flex horizontal crossaxis-center space="xs"> ${plain} </x-flex> `);
    return xComponents(html`
      <x-flex wrapper ${css({ position: "relative" })}>
        ${this._label()}
        <x-flex background horizontal crossaxis-center space="xs"> ${plain} </x-flex>
        ${this.info ? html` <x-tooltip .text=${html` ${unsafeHTML(this.info)} `}></x-tooltip> ` : nothing}
      </x-flex>
    `);
  }
  clearableIcon() {
    return this.clearable && this.showLabel && !this.disabled ? html`
          <x-icon
            ${css({ cursor: "pointer" })}
            @click=${(e) => {
      e.stopPropagation();
      this.dispatchEvent(new CustomEvent("cleared"));
    }}
          >
            close
          </x-icon>
        ` : nothing;
  }
  iconDisplay() {
    const icon = this.icon || (this.info && this.placeholderMode !== "static-floating" ? "info" : "");
    return icon ? html`
          <x-icon
            ${css({ cursor: "pointer" })}
            @click="${() => {
      this.dispatchEvent(new CustomEvent("icon-click"));
    }}"
          >
            ${icon}
            ${this.iconHoverTooltip ? html` <x-tooltip .text=${html` ${unsafeHTML(this.iconHoverTooltip)} `}> </x-tooltip> ` : nothing}
          </x-icon>
        ` : nothing;
  }
  _label() {
    if (!this.error && !this.placeholder)
      return nothing;
    if (this.placeholderMode === "static-floating") {
      return html`
        <x-flex
          horizontal
          space-between
          crossaxis-center
          space="sm"
          ${css({
        marginLeft: "0",
        marginBottom: this.spacing.xs,
        opacity: "1",
        transform: "translateY(0)",
        transition: "opacity .12s ease, transform .12s ease, max-height .12s ease, margin .12s ease",
        color: this.error ? this.colors.tone.error : void 0,
        userSelect: "none"
      })}
        >
          <x-text ${css({ color: this.colors.tone.placeholder, display: "inline-block" })}>
            ${this.placeholder}
          </x-text>
          ${this.error ? html`
                <x-text
                  ellipsis
                  flex
                  title=${this.error}
                  .value=${this.error}
                  ${css({ color: this.colors.tone.error, display: "inline-block" })}
                >
                </x-text>
              ` : nothing}
          ${this.info ? html`
                <x-flex center ${css({ height: "9px", color: this.colors.neutral.shade(50) })}>
                  <tr-icon> info </tr-icon>
                </x-flex>
              ` : nothing}
        </x-flex>
      `;
    }
    if (this.placeholderMode === "floating") {
      return html`
        <x-text
          ${css({
        marginLeft: this.spacing.sm,
        marginBottom: !!this.showLabel ? this.spacing.xs : "0",
        opacity: !!this.showLabel ? "1" : "0",
        transform: !!this.showLabel ? "translateY(0)" : "translateY(20px)",
        transition: "opacity .12s ease, transform .12s ease, max-height .12s ease, margin .12s ease",
        color: this.error ? this.colors.tone.error : void 0,
        pointerEvents: "none"
      })}
          tone="subtle"
        >
          ${this.error || this.placeholder}
        </x-text>
      `;
    } else if (this.placeholderMode === "inline-floating") {
      return html`
        <div
          ${css({
        position: "absolute",
        fontFamily: this.fontFamily,
        fontSize: this.baseFontSize + "px",
        userSelect: "none",
        marginLeft: this.spacing.sm,
        transformOrigin: "left top",
        top: "0",
        transform: this.showLabel ? "translateY(10%) scale(0.8, 0.8)" : "translateY(40%)",
        opacity: this.showLabel ? "1" : "0",
        transition: "transform 0.2s ease-out, opacity 0.15 linear",
        color: this.error ? this.colors.tone.error : this.colors.tone.placeholder,
        pointerEvents: "none",
        maxWidth: "100%",
        minWidth: "0",
        whiteSpace: "nowrap",
        overflowX: "hidden",
        overflowY: "visible",
        textOverflow: "ellipsis"
      })}
        >
          ${this.error || this.placeholder}
        </div>
      `;
    }
    return nothing;
  }
  _prefixLabel() {
    return this.placeholderMode === "prefix" && this.placeholder && this.showLabel ? html` <x-text flex-none tone="subtle"> ${this.placeholder} </x-text> ` : nothing;
  }
};
__decorateClass([
  property({ type: Boolean, reflect: true })
], XFormFieldWrapper.prototype, "transparent", 2);
__decorateClass([
  property({})
], XFormFieldWrapper.prototype, "placeholder", 2);
__decorateClass([
  property({ reflect: true, attribute: "placeholder-mode" })
], XFormFieldWrapper.prototype, "placeholderMode", 2);
__decorateClass([
  property({})
], XFormFieldWrapper.prototype, "icon", 2);
__decorateClass([
  property({ attribute: "icon-hover-tooltip" })
], XFormFieldWrapper.prototype, "iconHoverTooltip", 2);
__decorateClass([
  property()
], XFormFieldWrapper.prototype, "info", 2);
__decorateClass([
  property({ type: Boolean })
], XFormFieldWrapper.prototype, "clearable", 2);
__decorateClass([
  property({ type: Boolean })
], XFormFieldWrapper.prototype, "disabled", 2);
__decorateClass([
  property({})
], XFormFieldWrapper.prototype, "postfix", 2);
__decorateClass([
  property({ type: Boolean, reflect: true, attribute: "show-label" })
], XFormFieldWrapper.prototype, "showLabel", 2);
__decorateClass([
  property({ type: Boolean, reflect: true, attribute: "multi-line" })
], XFormFieldWrapper.prototype, "multiLine", 2);
__decorateClass([
  property()
], XFormFieldWrapper.prototype, "error", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], XFormFieldWrapper.prototype, "plain", 2);
XFormFieldWrapper = __decorateClass([
  customElement("x-formfield-wrapper")
], XFormFieldWrapper);
export {
  XFormFieldWrapper
};
